import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from './baseApi';

const dataBaseQuery = createBaseQuery('https://s3.ts1cloud.com/service3/');

// Server returns the freshly persisted group on POST/PATCH so we can splice
// it into the cached list optimistically — no waiting for refetch.
type ServerAlertGroup = {
  id: number | string;
  name: string;
  notifyTemperature: boolean;
  notifyBattery: boolean;
  notifyMissingData: boolean;
  sensors: number[];
  recipients: Array<{ id?: number; userId?: number | string; user_id?: number | string; email: boolean; sms: boolean; push: boolean }>;
  schedule?: { enabled: boolean; windows: { day: number; start: string; end: string }[] } | null;
};

/** Some responses omit recipients/sensors; table code expects arrays. */
function normalizeAlertGroupMerge<T extends Partial<ServerAlertGroup> & { id: number | string }>(data: T): T {
  return {
    ...data,
    recipients: Array.isArray(data.recipients) ? data.recipients : [],
    sensors: Array.isArray(data.sensors) ? data.sensors.map((s) => Number(s)) : [],
  };
}

type AlertGroupRequestFields = {
  name?: string;
  notify_temperature?: boolean;
  notify_battery?: boolean;
  notify_missing_data?: boolean;
  sensors?: unknown[];
  recipients?: unknown[];
  schedule?: { enabled: boolean; windows: { day: number; start: string; end: string }[] } | null;
};

/** Live POST/PATCH payloads sometimes return only id/message; splice request fields into the cache row. */
function mergeAlertGroupResponseWithRequest(
  data: Partial<ServerAlertGroup> & { id: number | string },
  req: AlertGroupRequestFields
): ReturnType<typeof normalizeAlertGroupMerge<Partial<ServerAlertGroup> & { id: number | string }>> {
  const d = data as Record<string, unknown>;
  const serverName = d.name ?? d.group_name;
  const name =
    typeof serverName === 'string' && serverName.trim() !== ''
      ? serverName.trim()
      : typeof req.name === 'string'
        ? req.name.trim()
        : '';

  const notifyTemperature =
    typeof data.notifyTemperature === 'boolean' ? data.notifyTemperature : req.notify_temperature !== false;
  const notifyBattery =
    typeof data.notifyBattery === 'boolean' ? data.notifyBattery : req.notify_battery !== false;
  const notifyMissingData =
    typeof data.notifyMissingData === 'boolean' ? data.notifyMissingData : req.notify_missing_data !== false;

  const sensors = Array.isArray(data.sensors)
    ? data.sensors.map((s) => Number(s))
    : Array.isArray(req.sensors)
      ? req.sensors.map((s) => Number(s))
      : [];

  const recipients = Array.isArray(data.recipients)
    ? data.recipients
    : Array.isArray(req.recipients)
      ? req.recipients.map((r: { userId?: unknown; user_id?: unknown; email?: unknown; sms?: unknown; push?: unknown }) => ({
          userId: r.userId ?? r.user_id,
          user_id: r.user_id ?? r.userId,
          email: !!r.email,
          sms: !!r.sms,
          push: !!r.push,
        }))
      : [];

  const schedule = data.schedule !== undefined ? data.schedule : (req.schedule !== undefined ? req.schedule : undefined);

  return normalizeAlertGroupMerge({
    ...data,
    name,
    notifyTemperature,
    notifyBattery,
    notifyMissingData,
    sensors,
    recipients,
    ...(schedule !== undefined ? { schedule } : {}),
  } as Partial<ServerAlertGroup> & { id: number | string });
}

export const dataApi = createApi({
  reducerPath: 'dataApi',
  baseQuery: dataBaseQuery,
  tagTypes: ['RemoteAlertGroups'],
  endpoints: (builder) => ({
    getRemoteAlertGroups: builder.query<any[], void>({
      query: () => ({
        url: 'remote-alert-groups',
        method: 'GET',
      }),
      providesTags: ['RemoteAlertGroups'],
    }),

    addRemoteAlertGroup: builder.mutation<ServerAlertGroup, any>({
      query: (group) => ({
        url: 'remote-alert-groups',
        method: 'POST',
        body: group,
      }),
      /** Do not invalidate on success — refetch races the POST body merge and CDN can briefly serve the old list. */
      invalidatesTags: (_result, error) => (error ? ['RemoteAlertGroups'] : []),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const tempId = `__pending_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        const optimisticRow = {
          id: tempId,
          name: arg.name ?? '',
          notifyTemperature: arg.notify_temperature !== false,
          notifyBattery: arg.notify_battery !== false,
          notifyMissingData: arg.notify_missing_data !== false,
          sensors: Array.isArray(arg.sensors) ? arg.sensors.map((s: unknown) => Number(s)) : [],
          recipients: Array.isArray(arg.recipients)
            ? arg.recipients.map((r: { userId?: unknown; user_id?: unknown; email?: unknown; sms?: unknown; push?: unknown }) => ({
                userId: r.userId ?? r.user_id,
                user_id: r.user_id ?? r.userId,
                email: !!r.email,
                sms: !!r.sms,
                push: !!r.push,
              }))
            : [],
        };

        const patchUndo = dispatch(
          dataApi.util.updateQueryData('getRemoteAlertGroups', undefined, (draft: unknown) => {
            if (!Array.isArray(draft)) return;
            draft.push(optimisticRow as typeof draft[number]);
          })
        );

        try {
          const { data } = await queryFulfilled;
          if (data && data.id != null) {
            dispatch(
              dataApi.util.updateQueryData('getRemoteAlertGroups', undefined, (draft: unknown) => {
                if (!Array.isArray(draft)) return;
                const pendingIdx = draft.findIndex((g: { id?: unknown }) => g.id === tempId);
                const byServerId = draft.findIndex((g: { id?: unknown }) => Number(g.id) === Number(data.id));
                const merged = mergeAlertGroupResponseWithRequest(data, arg);
                if (pendingIdx >= 0) {
                  draft[pendingIdx] = merged as never;
                  return;
                }
                if (byServerId >= 0) {
                  draft[byServerId] = merged as never;
                  return;
                }
                draft.push(merged as never);
              })
            );
          }
        } catch {
          patchUndo.undo();
        }
      },
    }),

    updateRemoteAlertGroup: builder.mutation<ServerAlertGroup, { id: number; body: any }>({
      query: ({ id, body }) => ({
        url: `remote-alert-groups/${encodeURIComponent(String(id))}`,
        // PUT duplicates PATCH server-side — some edges return 404 for PATCH (Worker notFound JSON).
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, error) => (error ? ['RemoteAlertGroups'] : []),
      async onQueryStarted({ id, body }, { dispatch, queryFulfilled }) {
        // Optimistic in-place update so the table + dialog reflect the new
        // name / notify flags / recipients the instant the user clicks Save.
        const patch = dispatch(
          dataApi.util.updateQueryData('getRemoteAlertGroups', undefined, (draft: any[]) => {
            if (!Array.isArray(draft)) return draft;
            const idx = draft.findIndex((g) => Number(g.id) === Number(id));
            if (idx < 0) return draft;
            draft[idx] = {
              ...draft[idx],
              name: body.name ?? draft[idx].name,
              notifyTemperature: body.notify_temperature !== false,
              notifyBattery: body.notify_battery !== false,
              notifyMissingData: body.notify_missing_data !== false,
              sensors: Array.isArray(body.sensors) ? body.sensors.map((s: any) => Number(s)) : draft[idx].sensors,
              recipients: Array.isArray(body.recipients)
                ? body.recipients.map((r: any) => ({
                    userId: r.userId ?? r.user_id,
                    user_id: r.user_id ?? r.userId,
                    email: !!r.email,
                    sms: !!r.sms,
                    push: !!r.push,
                  }))
                : draft[idx].recipients,
            };
          })
        );
        try {
          const { data } = await queryFulfilled;
          if (data && data.id != null) {
            // Replace optimistic snapshot with authoritative server data
            dispatch(
              dataApi.util.updateQueryData('getRemoteAlertGroups', undefined, (draft: any[]) => {
                if (!Array.isArray(draft)) return draft;
                const idx = draft.findIndex((g) => Number(g.id) === Number(data.id));
                if (idx >= 0) draft[idx] = mergeAlertGroupResponseWithRequest(data, body);
              })
            );
          }
        } catch {
          patch.undo();
        }
      },
    }),

    deleteRemoteAlertGroup: builder.mutation<any, number>({
      query: (id) => ({
        url: `remote-alert-groups/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, error) => (error ? ['RemoteAlertGroups'] : []),
      // Optimistic remove: drop the row immediately so the table reflects the
      // delete instantly. If the server rejects we put it back.
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          dataApi.util.updateQueryData('getRemoteAlertGroups', undefined, (draft: any[]) => {
            if (!Array.isArray(draft)) return draft;
            const idx = draft.findIndex((g) => Number(g.id) === Number(id));
            if (idx >= 0) draft.splice(idx, 1);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),
});

export const {
  useGetRemoteAlertGroupsQuery,
  useDeleteRemoteAlertGroupMutation,
  useAddRemoteAlertGroupMutation,
  useUpdateRemoteAlertGroupMutation,
} = dataApi;
